/**
 * Burned-in captions for in-browser export. Layout is a port of server
 * `_subtitle_render_spec` (+ helpers) and drawing mirrors `backend/caption_workers/captionHtml.js`,
 * which is what the server screenshots with Chromium.
 */

const REF_HEIGHT = 1080;
const MIN_FONT = 8;
const MAX_FONT = 96;
const MAX_PREVIEW_FONT = 96;
const MAX_OUTLINE = 20;
const LINE_HEIGHT = 1.28;
const DEVANAGARI_RE = /[\u0900-\u097F]/;
const LEGACY_LAYER_WIDTHS = new Set([0.82, 0.84, 0.86]);

const SAFE_INSETS = {
  '16:9': { top: 0.04, bottom: 0.06, left: 0.04, right: 0.04 },
  '21:9': { top: 0.05, bottom: 0.07, left: 0.06, right: 0.06 },
  '9:16': { top: 0.06, bottom: 0.08, left: 0.06, right: 0.06 },
  '4:5': { top: 0.05, bottom: 0.08, left: 0.05, right: 0.05 },
  '1:1': { top: 0.05, bottom: 0.07, left: 0.05, right: 0.05 },
  '4:3': { top: 0.04, bottom: 0.06, left: 0.04, right: 0.04 },
  '3:4': { top: 0.05, bottom: 0.07, left: 0.05, right: 0.05 },
  '2:3': { top: 0.05, bottom: 0.08, left: 0.06, right: 0.06 },
};

const ALLOWED_FONTS = new Set([
  'Arial', 'Helvetica', 'Georgia', 'Times New Roman', 'Verdana', 'Tahoma',
  'Trebuchet MS', 'Impact', 'Courier New', 'Comic Sans MS', 'Palatino Linotype',
  'Lucida Console', 'Noto Sans Devanagari',
]);

const FONT_STACKS = {
  'Noto Sans Devanagari': '"Noto Sans Devanagari", "Noto Sans", sans-serif',
  Arial: 'Arial, Helvetica, sans-serif',
  Helvetica: 'Helvetica, Arial, sans-serif',
  Georgia: 'Georgia, "Times New Roman", serif',
  'Times New Roman': '"Times New Roman", Times, serif',
  Verdana: 'Verdana, Geneva, sans-serif',
  Tahoma: 'Tahoma, Geneva, sans-serif',
  'Trebuchet MS': '"Trebuchet MS", Helvetica, sans-serif',
  Impact: 'Impact, Haettenschweiler, sans-serif',
  'Courier New': '"Courier New", Courier, monospace',
  'Comic Sans MS': '"Comic Sans MS", cursive, sans-serif',
  'Palatino Linotype': '"Palatino Linotype", Palatino, serif',
  'Lucida Console': '"Lucida Console", Monaco, monospace',
};

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function field(st, snake, camel, fallback) {
  if (st?.[snake] != null) return st[snake];
  if (st?.[camel] != null) return st[camel];
  return fallback;
}

function parseAspect(value) {
  const parts = String(value || '16:9').trim().replace('/', ':').split(':');
  const w = Number(parts[0]);
  const h = parts.length > 1 ? Number(parts[1]) : 9;
  return w > 0 && h > 0 ? w / h : 16 / 9;
}

function isPortrait(aspect) {
  return parseAspect(aspect) < 0.95;
}

function safeInsets(aspect) {
  const key = String(aspect || '16:9').trim();
  if (SAFE_INSETS[key]) return SAFE_INSETS[key];
  const ar = parseAspect(key);
  if (ar < 0.65) return SAFE_INSETS['9:16'];
  if (ar < 0.9) return SAFE_INSETS['4:5'];
  if (ar > 2.1) return SAFE_INSETS['21:9'];
  return SAFE_INSETS['16:9'];
}

function maxLayerWidth(aspect) {
  const s = safeInsets(aspect);
  return Math.max(0.18, Math.min(0.98, 1 - s.left - s.right));
}

function normalizeLayerWidth(width, aspect) {
  const maxW = maxLayerWidth(aspect);
  const w = Number(width);
  if (!Number.isFinite(w)) return maxW;
  if (LEGACY_LAYER_WIDTHS.has(Math.round(w * 100) / 100)) return maxW;
  return Math.max(0.18, Math.min(maxW, w));
}

export function subtitleLayerFromStyle(style, aspect) {
  const st = style && typeof style === 'object' ? style : {};
  if (st.layer && typeof st.layer === 'object') {
    return { ...st.layer, width: normalizeLayerWidth(st.layer.width ?? 0.86, aspect) };
  }
  const ax = num(field(st, 'anchor_x', 'anchorX', 0.5), 0.5);
  const ay = num(field(st, 'anchor_y', 'anchorY', 0.88), 0.88);
  const h = num(st.height, 0.14);
  return {
    x: ax,
    y: ay - h / 2,
    width: maxLayerWidth(aspect),
    height: h,
    rotation: num(st.rotation),
    text_align: st.text_align || st.textAlign || 'center',
  };
}

function horizontalPaddingPx(frameW, aspect) {
  const ar = parseAspect(aspect);
  let pct = 0.06;
  if (ar < 0.95) pct = 0.1;
  if (ar < 0.65) pct = 0.12;
  return Math.max(12, Math.round(Math.max(1, frameW) * pct));
}

export function subtitleFontPx(vw, vh, style, layer, aspect) {
  const st = style && typeof style === 'object' ? style : {};
  const bold = Boolean(field(st, 'bold', 'bold', false));
  const fs = num(field(st, 'font_size', 'fontSize', 22), 22);
  const base = Math.min(MAX_FONT, Math.max(MIN_FONT, fs));
  const h = Math.max(1, vh);
  const w = Math.max(1, vw);
  let px = (base / REF_HEIGHT) * h;
  const usableW = Math.max(40, w - horizontalPaddingPx(w, aspect) * 2);
  const charsCap = isPortrait(aspect) ? 20 : 34;
  px = Math.min(px, usableW / (charsCap * 0.52));
  px = Math.max(9, Math.min(MAX_PREVIEW_FONT, px));
  if (!layer) return Math.round(px);
  const boxH = Math.max(20, num(layer.height, 0.14) * h);
  const boxW = Math.max(32, num(layer.width, 0.86) * w);
  px = Math.min(px, (boxH / 1.35) * (bold ? 0.92 : 1), boxW / 8);
  return Math.max(MIN_FONT, Math.min(MAX_FONT, Math.round(px)));
}

function graphemes(text) {
  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    return [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)].map((s) => s.segment);
  }
  return [...text];
}

function glen(text) {
  return graphemes(text || '').length;
}

function gtruncate(text, maxLen, suffix = '…') {
  const clusters = graphemes(text || '');
  if (clusters.length <= maxLen) return text || '';
  return clusters.slice(0, Math.max(1, maxLen - glen(suffix))).join('') + suffix;
}

export function wrapSubtitleLines(text, maxLines, charsPerLine) {
  const raw = String(text || '').trim();
  if (!raw) return [];
  const maxL = Math.max(1, Math.trunc(maxLines));
  const cpl = Math.max(8, Math.trunc(charsPerLine));
  if (glen(raw) <= cpl) return [raw];
  const words = raw.split(/\s+/);
  const lines = [];
  let current = '';
  for (let i = 0; i < words.length; i += 1) {
    const word = words[i];
    const candidate = current ? `${current} ${word}`.trim() : word;
    if (glen(candidate) <= cpl) {
      current = candidate;
      continue;
    }
    if (current) {
      lines.push(current);
      current = word;
    } else if (glen(word) > cpl) {
      lines.push(gtruncate(word, cpl));
      current = '';
    } else {
      current = word;
    }
    if (lines.length >= maxL) {
      const tail = [current, ...words.slice(i + 1)].filter(Boolean).join(' ').trim();
      if (tail) {
        const last = lines[maxL - 1] || '';
        const merged = last ? `${last} ${tail}`.trim() : tail;
        lines[maxL - 1] = glen(merged) <= cpl ? merged : gtruncate(merged, cpl);
      }
      return lines;
    }
  }
  if (current && lines.length < maxL) lines.push(current);
  return lines.length ? lines : [gtruncate(raw, cpl)];
}

/** Server `_subtitle_render_spec` for one cue at `vw×vh`. */
export function buildCaptionSpec(cue, style, vw, vh, aspect) {
  const st = style && typeof style === 'object' ? style : {};
  const layer = subtitleLayerFromStyle(st, aspect);
  const fs = subtitleFontPx(vw, vh, st, layer, aspect);
  const text = String(cue?.text || '').replace(/\r\n?/g, '\n').normalize('NFC');

  const bw = num(layer.width, 0.86);
  const bh = num(layer.height, 0.14);
  const boxW = bw * vw;
  const boxH = bh * vh;
  const left = Math.max(0, Math.round(num(layer.x, 0.5) * vw - boxW / 2));
  const top = Math.max(0, Math.round(num(layer.y, 0.82) * vh - boxH / 2));

  let lines;
  if (Array.isArray(cue?.lines) && cue.lines.length) {
    lines = cue.lines.map((l) => String(l).normalize('NFC').trim()).filter(Boolean);
  } else {
    const portrait = isPortrait(aspect);
    const maxLines = Math.max(1, Math.min(portrait ? 4 : 5, Math.trunc(Math.max(20, boxH) / (fs * 1.32))));
    const cpl = Math.max(10, Math.trunc(Math.max(32, boxW) / (Math.max(9, fs) * 0.48)));
    lines = wrapSubtitleLines(text, maxLines, cpl);
  }

  const ow = num(field(st, 'outline_width', 'outlineWidth', 2), 2);
  const fontPref = String(field(st, 'font_family', 'fontFamily', 'Arial') || 'Arial').trim();
  return {
    text,
    lines,
    boxLeft: left,
    boxTop: top,
    boxWidth: Math.max(1, Math.round(boxW)),
    boxHeight: Math.max(1, Math.round(boxH)),
    fontSize: fs,
    fontFamily: DEVANAGARI_RE.test(text) ? 'Noto Sans Devanagari' : (ALLOWED_FONTS.has(fontPref) ? fontPref : 'Arial'),
    textAlign: String(layer.text_align || layer.textAlign || 'center').toLowerCase(),
    rotation: num(layer.rotation),
    color: field(st, 'color', 'color', '#ffffff'),
    outlineColor: field(st, 'outline_color', 'outlineColor', '#000000'),
    outlineWidth: Math.max(0, Math.min(MAX_OUTLINE, Math.round((ow * vh) / REF_HEIGHT))),
    bold: Boolean(field(st, 'bold', 'bold', false)),
    italic: Boolean(field(st, 'italic', 'italic', false)),
    underline: Boolean(field(st, 'underline', 'underline', false)),
    letterSpacing: num(field(st, 'letter_spacing', 'letterSpacing', 0)),
    shadowColor: field(st, 'shadow_color', 'shadowColor', '#ffff00'),
    shadowBlur: num(field(st, 'shadow_blur', 'shadowBlur', 0)),
    backgroundColor: field(st, 'background_color', 'backgroundColor', '#000000'),
    backgroundOpacity: num(field(st, 'background_opacity', 'backgroundOpacity', 0)),
    highlightIndex: Number.isFinite(Number(cue?.highlight_index)) ? Number(cue.highlight_index) : -1,
    highlightColor: String(cue?.highlight_color || field(st, 'spoken_word_highlight_color', 'spokenWordHighlightColor', '#d4ff00')),
  };
}

export function hexToRgba(hex, alpha = 1) {
  let raw = String(hex || '#000000').trim().replace('#', '');
  if (raw.length === 3) raw = raw.split('').map((c) => c + c).join('');
  if (raw.length !== 6) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function captionFontCss(spec) {
  const stack = FONT_STACKS[spec.fontFamily] || FONT_STACKS.Arial;
  const weight = spec.highlightIndex >= 0 || spec.bold ? 800 : 600;
  return `${spec.italic ? 'italic ' : ''}${weight} ${spec.fontSize}px ${stack}`;
}

function paintGlyph(ctx, word, x, y, spec, paint) {
  const { glow, outline, offsets } = paint;
  if (glow) {
    const far = 100000;
    ctx.save();
    ctx.shadowColor = glow;
    ctx.shadowBlur = spec.shadowBlur;
    ctx.shadowOffsetX = far;
    ctx.fillStyle = glow;
    ctx.fillText(word, x - far, y);
    ctx.restore();
  }
  if (offsets.length) {
    ctx.fillStyle = outline;
    offsets.forEach(([dx, dy]) => ctx.fillText(word, x + dx, y + dy));
  }
  ctx.fillStyle = spec.color || '#ffffff';
  ctx.fillText(word, x, y);
}

function drawKaraokeLine(ctx, line, spec, anchorX, y, align, paint) {
  const words = String(line || '').toLocaleUpperCase().split(/\s+/).filter(Boolean);
  if (!words.length) return;
  ctx.save();
  ctx.textAlign = 'left';
  const gap = ctx.measureText(' ').width;
  const widths = words.map((word) => ctx.measureText(word).width);
  const total = widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, words.length - 1);
  const start = align === 'right' ? anchorX - total : align === 'left' ? anchorX : anchorX - total / 2;
  const padX = spec.fontSize * 0.12;
  const boxTop = y - spec.fontSize * 0.62;
  const boxH = spec.fontSize * 1.2;
  let cursor = start;
  words.forEach((word, index) => {
    if (index === spec.highlightIndex) {
      ctx.fillStyle = spec.highlightColor || '#d4ff00';
      ctx.fillRect(cursor - padX, boxTop, widths[index] + padX * 2, boxH);
    }
    cursor += widths[index] + gap;
  });
  cursor = start;
  words.forEach((word, index) => {
    paintGlyph(ctx, word, cursor, y, spec, paint);
    cursor += widths[index] + gap;
  });
  ctx.restore();
}

/** Draw a caption spec onto a full-frame context (same box model as captionHtml.js). */
export function drawCaption(ctx, spec) {
  const lines = spec.lines?.length ? spec.lines : [spec.text].filter(Boolean);
  if (!lines.length) return;
  const { boxLeft, boxTop, boxWidth, boxHeight, fontSize } = spec;
  const lh = fontSize * LINE_HEIGHT;
  const hasBg = spec.backgroundOpacity > 0.02;
  const padX = hasBg ? 10 : 0;
  const padY = hasBg ? 4 : 0;
  const innerLeft = boxLeft + 4;
  const innerW = Math.max(1, boxWidth - 8);
  const innerTop = boxTop + 2;
  const innerH = Math.max(1, boxHeight - 4);
  const contentH = lines.length * lh + padY * 2;
  const contentTop = innerTop + (innerH - contentH) / 2;

  ctx.save();
  if (spec.rotation) {
    const cx = boxLeft + boxWidth / 2;
    const cy = boxTop + boxHeight / 2;
    ctx.translate(cx, cy);
    ctx.rotate((spec.rotation * Math.PI) / 180);
    ctx.translate(-cx, -cy);
  }
  ctx.beginPath();
  ctx.rect(boxLeft, boxTop, boxWidth, boxHeight);
  ctx.clip();

  if (hasBg) {
    ctx.fillStyle = hexToRgba(spec.backgroundColor, Math.min(1, spec.backgroundOpacity));
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') ctx.roundRect(innerLeft, contentTop, innerW, contentH, 6);
    else ctx.rect(innerLeft, contentTop, innerW, contentH);
    ctx.fill();
  }

  ctx.font = captionFontCss(spec);
  if ('letterSpacing' in ctx) ctx.letterSpacing = `${spec.letterSpacing || 0}px`;
  ctx.textBaseline = 'middle';
  const align = ['left', 'right', 'center'].includes(spec.textAlign) ? spec.textAlign : 'center';
  ctx.textAlign = align;
  const x = align === 'left'
    ? innerLeft + padX
    : align === 'right' ? innerLeft + innerW - padX : innerLeft + innerW / 2;

  const ow = spec.outlineWidth;
  const outline = hexToRgba(spec.outlineColor, 1);
  const glow = spec.shadowBlur > 0 ? hexToRgba(spec.shadowColor, 0.85) : null;
  const offsets = ow > 0
    ? [[-ow, 0], [ow, 0], [0, -ow], [0, ow], [-ow, -ow], [ow, -ow], [-ow, ow], [ow, ow]]
    : [];

  const karaoke = Number.isFinite(spec.highlightIndex) && spec.highlightIndex >= 0 && lines.length === 1;
  lines.forEach((line, i) => {
    const y = contentTop + padY + lh * i + lh / 2;
    if (karaoke && i === 0) {
      drawKaraokeLine(ctx, line, spec, x, y, align, { glow, outline, offsets });
      return;
    }
    if (glow) {
      const far = 100000;
      ctx.save();
      ctx.shadowColor = glow;
      ctx.shadowBlur = spec.shadowBlur;
      ctx.shadowOffsetX = far;
      ctx.fillStyle = glow;
      ctx.fillText(line, x - far, y);
      ctx.restore();
    }
    if (offsets.length) {
      ctx.fillStyle = outline;
      for (const [dx, dy] of offsets) ctx.fillText(line, x + dx, y + dy);
    }
    ctx.fillStyle = spec.color || '#ffffff';
    ctx.fillText(line, x, y);
    if (spec.underline) {
      const w = ctx.measureText(line).width;
      const startX = align === 'left' ? x : align === 'right' ? x - w : x - w / 2;
      ctx.fillRect(startX, y + fontSize * 0.42, w, Math.max(1, Math.round(fontSize / 14)));
    }
  });
  ctx.restore();
}

/** Load caption web fonts before drawing so the first frames don't use a fallback face. */
export async function preloadCaptionFonts(specs) {
  if (typeof document === 'undefined' || !document.fonts?.load) return;
  const seen = new Set();
  await Promise.all((specs || []).map((spec) => {
    const css = captionFontCss(spec);
    if (seen.has(css)) return null;
    seen.add(css);
    return document.fonts.load(css, spec.text || 'A').catch(() => null);
  }));
}
